import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2} from '@angular/core';
import {AsyncPipe, NgForOf, NgIf} from "@angular/common";
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {filter, Observable, Subject, Subscription, take} from "rxjs";
import {ReportService} from "../../../services/report.service";
import {PopupService} from "../../../services/popup.service";
import {Store} from "@ngrx/store";
import {ReportState} from "../../../state/report/report.reducer";
import {selectShareReportFailure, selectShareReportSuccess} from "../../../state/report/report.selectors";
import {shareReport} from "../../../state/report/report.actions";
import { dashboardInfo, services, storeService } from "../../../state/dashboard/dashboard.actions";
import {
  selectDashboardInfoFailure,
  selectDashboardInfoSuccess,
  selectServicesSuccess, selectStoreService, selectStoreServiceError
} from "../../../state/dashboard/dashboard.selectors";
import {takeUntil} from "rxjs/operators";
import {selectPsyTestError, selectPsyTestResult} from "../../../state/psyTesting/psyTesting.selectors";

@Component({
  selector: 'app-request-assistance',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
    NgForOf
  ],
  templateUrl: './request-assistance.component.html',
  styleUrl: './request-assistance.component.scss'
})
export class RequestAssistanceComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() isVisible: boolean = false;
  @Output() modalClosed: EventEmitter<void> = new EventEmitter();

  form: FormGroup;

  reportSubscription: Subscription;
  report: any;

  loading$: Observable<boolean>;

  services: any[] = [];

  constructor(
    public formBuilder: FormBuilder,
    private reportService: ReportService,
    private popupService: PopupService,
    private store: Store<{ report: ReportState }>,
    private renderer: Renderer2
  ) {

    this.reportSubscription = reportService.report$.subscribe(
      report => { this.report = report; }
    );

    this.form = formBuilder.group(
      {
        service_id: ['', Validators.required],
        message: ['', Validators.required],
      },
      { updateOn: "blur" }
    );

    this.loading$ = this.store.select(state => state.report.loading);

  }

  ngOnInit(): void {
    this.getServicesList();

    this.toggleBodyScroll(this.isVisible);
  }

  ngOnDestroy(): void {
    this.toggleBodyScroll(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(): void {
    this.toggleBodyScroll(this.isVisible);

    if (this.isVisible) {
      this.form.reset({
        service_id: '', // Reset to empty string to show placeholder
        message: ''     // Reset other fields as well
      });
    }
  }

  closeModal() {
    this.isVisible = false;
    this.modalClosed.emit(); // Notify the parent component that the modal has been closed

    // Reset form to initial state
    this.form.reset({
      service_id: '', // Reset to empty string to show placeholder
      message: ''     // Reset other fields as well
    });
  }

  private toggleBodyScroll(block: boolean): void {
    if (block) {
      this.renderer.addClass(document.body, 'overflow-hidden');
    } else {
      this.renderer.removeClass(document.body, 'overflow-hidden');
    }
  }

  getServicesList() {
    // Dispatch the action to fetch dashboard info
    this.store.dispatch(services());

    // Listen for success response
    this.store
      .select(selectServicesSuccess)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (result) => {
          if (result) {
            this.services = result;
          }
        },
        error: (error) => {
          console.error('Error loading dashboard info:', error);
        },
      });

    // Handle failure response
    this.store
      .select(selectDashboardInfoFailure)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (error) => {
          if (error) {
            console.error('Failed to load dashboard info:', error);
            // Optional: Show a notification or take other appropriate actions
          }
        },
      });
  }

  storeService() {
    if (this.form.invalid) {
      // Mark all fields as touched to trigger validation errors
      this.form.markAllAsTouched();
      return;
    }

    const serviceId = this.form.get('service_id')?.value;
    const message = this.form.get('message')?.value;

    console.log('Submitting form with:', { serviceId, message });

    // Dispatch the action to store the service
    this.store.dispatch(storeService({ serviceId: serviceId, message }));

    // Observe success and error streams
    this.store
      .select(selectStoreService)
      .pipe(
        filter((result) => !!result),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: () => {
          this.popupService.notify('Request assistance successfully sent!', 'Success', 'success');
          this.closeModal(); // Close the modal
        },
        error: (error) => {
          console.error('Store service success subscription error:', error);
        },
      });

    this.store
      .select(selectStoreServiceError)
      .pipe(
        filter((error) => !!error),
        take(1) // Automatically unsubscribe after one emission
      )
      .subscribe({
        next: (error) => {
          const errorMessage = error.message || 'Failed to store the service.';
          this.popupService.notify(errorMessage, 'Error', 'error');
        },
        error: (error) => {
          console.error('Store service error subscription error:', error);
        },
      });
  }

}
